import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Row, Spin, Typography, message } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import {
  Bar, BarChart, CartesianGrid, Cell, Line, LineChart, Pie, PieChart,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import dayjs from 'dayjs';
import MonthSelector from '../layout/MonthSelector';
import api from '../common/api';
import { useCashFlow } from '../common/CashFlowContext';
import { downloadCashFlowExcel } from '../common/excelExport';

const fmt = (n) => new Intl.NumberFormat('ko-KR').format(n);
const COLORS = ['#4A90D9', '#E53935', '#43A047', '#FB8C00', '#8E24AA', '#00ACC1', '#F4511E', '#3949AB'];

function SectionCard({ title, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <Typography.Title level={5} style={{ marginBottom: 8 }}>{title}</Typography.Title>
      <Card bodyStyle={{ padding: 16 }}>{children}</Card>
    </div>
  );
}

function SummaryStats({ monthly, currentUserId }) {
  const mine = currentUserId === 1 ? monthly.user1_amount : monthly.user2_amount;
  const other = currentUserId === 1 ? monthly.user2_amount : monthly.user1_amount;
  const items = [
    { label: '나', value: mine },
    { label: '상대', value: other },
    { label: '합산', value: monthly.total_amount },
  ];
  return (
    <Row gutter={16}>
      {items.map((it) => (
        <Col span={8} key={it.label} style={{ textAlign: 'center' }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>{it.label}</Typography.Text>
          <div style={{ fontSize: 15, fontWeight: 'bold', marginTop: 4 }}>{fmt(it.value || 0)}원</div>
        </Col>
      ))}
    </Row>
  );
}

function CategoryPie({ data }) {
  const total = data.reduce((s, c) => s + c.total_amount, 0);
  return (
    <Row gutter={16} align="middle">
      <Col span={10}>
        <ResponsiveContainer width="100%" height={160}>
          <PieChart>
            <Pie
              data={data}
              dataKey="total_amount"
              nameKey="category_name"
              innerRadius={40}
              outerRadius={70}
              paddingAngle={2}
            >
              {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
            </Pie>
            <Tooltip formatter={(v) => `${fmt(v)}원`} />
          </PieChart>
        </ResponsiveContainer>
      </Col>
      <Col span={14}>
        {data.map((c, i) => {
          const pct = total > 0 ? ((c.total_amount / total) * 100).toFixed(1) : '0';
          return (
            <div key={c.category_id} style={{ display: 'flex', alignItems: 'center', padding: '3px 0' }}>
              <div style={{
                width: 10, height: 10, borderRadius: '50%',
                background: COLORS[i % COLORS.length], marginRight: 6,
              }} />
              <Typography.Text style={{ flex: 1, fontSize: 12 }}>{c.category_name}</Typography.Text>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>{pct}%</Typography.Text>
            </div>
          );
        })}
      </Col>
    </Row>
  );
}

function MethodList({ data }) {
  return (
    <div>
      {data.map((m) => (
        <div key={m.method_id} style={{
          display: 'flex', justifyContent: 'space-between', padding: '6px 0',
        }}>
          <Typography.Text>{m.method_name}</Typography.Text>
          <Typography.Text strong>{fmt(m.amount)}원</Typography.Text>
        </div>
      ))}
    </div>
  );
}

function DayLineChart({ data }) {
  const chartData = data.map((d) => ({ day: dayjs(d.date).date(), amount: d.amount }));
  return (
    <ResponsiveContainer width="100%" height={210}>
      <LineChart data={chartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="day" tickFormatter={(v) => `${v}일`} fontSize={10} />
        <YAxis tickFormatter={(v) => fmt(v)} fontSize={10} width={60} />
        <Tooltip formatter={(v) => `${fmt(v)}원`} labelFormatter={(v) => `${v}일`} />
        <Line type="monotone" dataKey="amount" stroke="#4A90D9" fill="#4A90D9" dot={false} strokeWidth={2} />
      </LineChart>
    </ResponsiveContainer>
  );
}

function WeeklyBarChart({ weeks }) {
  const chartData = weeks.map((w) => ({
    name: `${w.week}주`,
    amount: w.total_amount,
  }));
  return (
    <ResponsiveContainer width="100%" height={180}>
      <BarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey="name" fontSize={11} />
        <YAxis tickFormatter={(v) => fmt(v)} fontSize={10} width={60} />
        <Tooltip formatter={(v) => `${fmt(v)}원`} />
        <Bar dataKey="amount" fill="#4A90D9" radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export default function StatsScreen() {
  const { currentUserId, yearMonth, users } = useCashFlow();
  const [monthly, setMonthly] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const [m, w] = await Promise.all([
        api.get('/stats/monthly', { params: { year: yearMonth.year, month: yearMonth.month } }),
        api.get('/stats/weekly', { params: { year: yearMonth.year, month: yearMonth.month } }),
      ]);
      setMonthly(m.data);
      setWeekly(w.data);
    } finally {
      setLoading(false);
    }
  }, [yearMonth]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const expenseRes = await api.get('/expenses', {
        params: { year: yearMonth.year, month: yearMonth.month },
      });
      downloadCashFlowExcel({
        year: yearMonth.year,
        month: yearMonth.month,
        monthly,
        weekly,
        expenses: expenseRes.data,
        users,
      });
    } catch (err) {
      message.error('엑셀 다운로드 실패: ' + (err?.message || '알 수 없는 오류'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: '0 auto' }}>
      <MonthSelector />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button
          icon={<DownloadOutlined />}
          onClick={handleDownload}
          loading={downloading}
          disabled={loading || !monthly}
        >
          엑셀 다운로드
        </Button>
      </div>
      {loading || !monthly ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : (
        <>
          <SectionCard title="이번 달 합계">
            <SummaryStats monthly={monthly} currentUserId={currentUserId} />
          </SectionCard>
          {monthly.by_category?.length > 0 && (
            <SectionCard title="카테고리별">
              <CategoryPie data={monthly.by_category} />
            </SectionCard>
          )}
          {monthly.by_method?.length > 0 && (
            <SectionCard title="결제수단별">
              <MethodList data={monthly.by_method} />
            </SectionCard>
          )}
          {monthly.by_day?.length > 0 && (
            <SectionCard title="일별 지출">
              <DayLineChart data={monthly.by_day} />
            </SectionCard>
          )}
          {weekly?.weeks?.length > 0 && (
            <SectionCard title="주별 지출">
              <WeeklyBarChart weeks={weekly.weeks} />
            </SectionCard>
          )}
        </>
      )}
    </div>
  );
}
